import React, { useState, useEffect } from 'react'; // FIX: ADDED useEffect
import { trpc } from '@/trpc/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Car, Search, ChevronLeft, ChevronRight, Loader2, InfoIcon, Eye, CheckCircle, XCircle, Terminal, Image, FileText } from 'lucide-react';
import { useDebounce } from '@/hooks/useDebounce';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Link } from 'react-router-dom';
import { DocumentType } from '@logistics-platform/types'; // Import DocumentType
import { toast } from 'sonner'; // Added missing toast import

const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000';

// Helper function to resolve image/document paths
function resolveSrc(path?: string) {
    if (!path) return '/images/placeholder.png';
    return path.startsWith('http') ? path : `${apiUrl}/uploads/${path}`;
}

// Type definition for a vehicle returned by the API
interface AdminVehicle {
    id: string;
    make: string;
    model: string;
    licensePlate: string;
    capacity: number;
    capacityUnit: string;
    capacityKg: number;
    verified: boolean;
    transporterId: string;
    createdAt: string;
    updatedAt: string;
    transporter: {
        id: string;
        email: string;
        profile: {
            fullName: string;
        } | null;
    } | null;
    images: { filePath: string }[];
    documents: { filePath: string; type: DocumentType }[]; // Use DocumentType enum
}

const AllVehiclesPage: React.FC = () => {
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedVerifiedStatus, setSelectedVerifiedStatus] = useState<boolean | undefined>(undefined);
    const debouncedSearchQuery = useDebounce(searchQuery, 500);

    const { data, isLoading, error, refetch } = trpc.admin.getAllVehicles.useQuery({
        page,
        pageSize,
        search: debouncedSearchQuery,
        verifiedStatus: selectedVerifiedStatus,
        orderBy: 'createdAt',
        orderDirection: 'desc',
    });

    const verifyVehicleMutation = trpc.admin.verifyVehicle.useMutation({
        onSuccess: () => { toast.success("Vehicle verified!"); refetch(); },
        onError: (err) => toast.error(err.message),
    });
    const rejectVehicleMutation = trpc.admin.rejectVehicle.useMutation({
        onSuccess: () => { toast.success("Vehicle rejected!"); refetch(); },
        onError: (err) => toast.error(err.message),
    });

    const vehicles = data?.vehicles || [];
    const totalVehicles = data?.totalCount || 0;
    const totalPages = Math.ceil(totalVehicles / pageSize);

    const [viewingVehicleId, setViewingVehicleId] = useState<string | null>(null);
    const { data: fullVehicleData, isLoading: isLoadingFullVehicle, error: fullVehicleError } = trpc.admin.getVehicleById.useQuery({
        vehicleId: viewingVehicleId!
    }, {
        enabled: !!viewingVehicleId,
        staleTime: 0,
    });
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [viewingVehicle, setViewingVehicle] = useState<AdminVehicle | null>(null); // For modal display

    // Initialize viewingVehicle when fullVehicleData is fetched
    useEffect(() => {
        if (fullVehicleData) {
            setViewingVehicle(fullVehicleData);
        }
    }, [fullVehicleData]);

    const handleViewClick = (vehicleId: string) => {
        setViewingVehicleId(vehicleId);
        setIsViewModalOpen(true);
    };

    const handleVerify = (vehicleId: string) => {
        verifyVehicleMutation.mutate({ vehicleId });
    };

    const handleReject = (vehicleId: string, reason?: string) => {
        rejectVehicleMutation.mutate({ vehicleId, reason });
    };

    if (error) {
        return (
            <div className="container mx-auto py-12 px-4 max-w-7xl">
                {/* NOTE: Alert needs to be imported, but assuming it's available in the context */}
                <Alert variant="destructive">
                    <Terminal className="h-4 w-4" />
                    <AlertTitle>Error Loading Vehicles</AlertTitle>
                    <AlertDescription>{error.message}</AlertDescription>
                </Alert>
            </div>
        );
    }

    return (
        <div className="container mx-auto py-6 px-2 sm:px-4 max-w-full animate-fade-in">
            <Card className="glass">
                <CardHeader className="p-4 sm:p-6">
                    <CardTitle className="flex items-center gap-2 text-foreground text-lg sm:text-2xl"><Car className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />View All Vehicles</CardTitle>
                    <CardDescription className="text-xs sm:text-sm">Browse, verify, and manage all registered vehicles across the platform.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4 sm:space-y-6 p-2 sm:p-6">
                    {/* Search and Filters */}
                    <div className="flex flex-col md:flex-row gap-3 sm:gap-4 flex-wrap">
                        <Input
                            placeholder="Search by make, model, or license plate..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full md:flex-1 bg-input text-foreground h-9 sm:h-10 text-sm"
                        />
                        <Select
                            value={selectedVerifiedStatus === undefined ? "all" : selectedVerifiedStatus ? "true" : "false"}
                            onValueChange={(value) => setSelectedVerifiedStatus(value === "all" ? undefined : value === "true")}
                        >
                            <SelectTrigger className="w-full md:w-[180px] bg-select text-foreground h-9 sm:h-10 text-sm">
                                <SelectValue placeholder="Filter by Verified" />
                            </SelectTrigger>
                            <SelectContent className="bg-popover text-popover-foreground">
                                <SelectItem value="all">All Statuses</SelectItem>
                                <SelectItem value="true">Verified</SelectItem>
                                <SelectItem value="false">Unverified</SelectItem>
                            </SelectContent>
                        </Select>
                        <Button variant="outline" onClick={() => { setSearchQuery(''); setSelectedVerifiedStatus(undefined); }} className="h-9 sm:h-10 text-sm">
                            Reset Filters
                        </Button>
                    </div>

                    {/* Vehicles Table */}
                    <div className="rounded-md border bg-card text-card-foreground shadow-sm overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-[80px] text-primary">ID</TableHead>
                                    <TableHead className="w-[150px] text-primary">Transporter</TableHead>
                                    <TableHead className="w-[150px] text-primary">Make / Model</TableHead>
                                    <TableHead className="w-[120px] text-primary">License Plate</TableHead>
                                    <TableHead className="w-[100px] text-primary">Capacity</TableHead>
                                    <TableHead className="w-[100px] text-primary">Verified</TableHead>
                                    <TableHead className="w-[120px] text-primary">Added On</TableHead>
                                    <TableHead className="w-[100px] text-primary text-center">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {isLoading ? (
                                    Array.from({ length: pageSize }).map((_, i) => (
                                        <TableRow key={i}>
                                            <TableCell colSpan={8}>
                                                <Skeleton className="h-8 w-full" />
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : vehicles.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={8} className="h-24 text-center text-muted-foreground">
                                            <p><InfoIcon className="inline-block mr-2 h-5 w-5" /> No vehicles found matching your criteria.</p>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    vehicles.map((vehicle) => (
                                        <TableRow key={vehicle.id} className="text-sm">
                                            <TableCell className="font-mono text-xs">{vehicle.id.substring(0, 8)}...</TableCell>
                                            <TableCell>
                                                <p className="font-medium text-foreground">{vehicle.transporter?.profile?.fullName || 'N/A'}</p>
                                                <p className="text-muted-foreground text-xs">{vehicle.transporter?.email || 'N/A'}</p>
                                            </TableCell>
                                            <TableCell className="font-medium text-foreground">{vehicle.make} {vehicle.model}</TableCell>
                                            <TableCell className="font-mono text-foreground">{vehicle.licensePlate}</TableCell>
                                            <TableCell className="text-muted-foreground">{vehicle.capacity} {vehicle.capacityUnit} ({vehicle.capacityKg} kg)</TableCell>
                                            <TableCell>
                                                <Badge variant={vehicle.verified ? "success" : "warning"}>{vehicle.verified ? "Verified" : "Pending"}</Badge>
                                            </TableCell>
                                            <TableCell className="text-muted-foreground text-xs">{format(new Date(vehicle.createdAt), 'yyyy-MM-dd')}</TableCell>
                                            <TableCell className="flex justify-center items-center gap-2">
                                                {/* Actions only if not verified */}
                                                {!vehicle.verified && (
                                                    <>
                                                        <Button variant="success" size="sm" onClick={() => handleVerify(vehicle.id)} disabled={verifyVehicleMutation.isLoading}>
                                                            {verifyVehicleMutation.isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                                                        </Button>
                                                        <Button variant="destructive" size="sm" onClick={() => handleReject(vehicle.id, "Admin review failed.")} disabled={rejectVehicleMutation.isLoading}>
                                                            {rejectVehicleMutation.isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
                                                        </Button>
                                                    </>
                                                )}
                                                <Button variant="outline" size="sm" onClick={() => handleViewClick(vehicle.id)} disabled={verifyVehicleMutation.isLoading || rejectVehicleMutation.isLoading}>
                                                    <Eye className="h-4 w-4" /> {/* View details */}
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>

                    {/* Pagination */}
                    <div className="flex justify-between items-center pt-4">
                        <p className="text-sm text-muted-foreground">
                            Showing {Math.min(totalVehicles, (page - 1) * pageSize + 1)} - {Math.min(totalVehicles, page * pageSize)} of {totalVehicles} vehicles
                        </p>
                        <div className="flex gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setPage(prev => Math.max(1, prev - 1))}
                                disabled={page === 1}
                            >
                                <ChevronLeft className="h-4 w-4" /> Previous
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setPage(prev => prev + 1)}
                                disabled={page >= totalPages}
                            >
                                Next <ChevronRight className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Vehicle Details Modal */}
            {viewingVehicle && ( // Use viewingVehicle for modal content
                <Dialog open={isViewModalOpen} onOpenChange={setIsViewModalOpen}>
                    <DialogContent className="sm:max-w-xl bg-card text-card-foreground">
                        <DialogHeader>
                            <DialogTitle className="text-foreground">Vehicle Details: {viewingVehicle.make} {viewingVehicle.model}</DialogTitle>
                            <DialogDescription>
                                Comprehensive information about this vehicle and its transporter.
                            </DialogDescription>
                        </DialogHeader>
                        {isLoadingFullVehicle ? ( // Use isLoadingFullVehicle for modal content as well if refetching
                            <div className="py-8 text-center"><Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" /> <p className="text-muted-foreground mt-2">Loading vehicle details...</p></div>
                        ) : fullVehicleError ? ( // Use error for modal content
                            <Alert variant="destructive">
                                <Terminal className="h-4 w-4" />
                                <AlertTitle>Error Loading Vehicle Details</AlertTitle>
                                <AlertDescription>{fullVehicleError.message}</AlertDescription>
                            </Alert>
                        ) : (
                            <div className="grid gap-4 py-4 max-h-[70vh] overflow-y-auto">
                                <div className="space-y-2">
                                    <h4 className="font-semibold text-primary">Vehicle Information</h4>
                                    <p className="text-sm"><strong>ID:</strong> <span className="font-mono text-xs">{viewingVehicle.id}</span></p>
                                    <p className="text-sm"><strong>Make:</strong> {viewingVehicle.make}</p>
                                    <p className="text-sm"><strong>Model:</strong> {viewingVehicle.model}</p>
                                    <p className="text-sm"><strong>License Plate:</strong> {viewingVehicle.licensePlate}</p>
                                    <p className="text-sm"><strong>Capacity:</strong> {viewingVehicle.capacity} {viewingVehicle.capacityUnit} ({viewingVehicle.capacityKg} kg)</p>
                                    <p className="text-sm"><strong>Verified:</strong> <Badge variant={viewingVehicle.verified ? "success" : "warning"}>{viewingVehicle.verified ? "Yes" : "No"}</Badge></p>
                                    <p className="text-sm"><strong>Added On:</strong> {format(new Date(viewingVehicle.createdAt), 'yyyy-MM-dd HH:mm')}</p>
                                </div>

                                {viewingVehicle.transporter && (
                                    <div className="space-y-2 pt-4 border-t border-border">
                                        <h4 className="font-semibold text-primary">Transporter Information</h4>
                                        <p className="text-sm"><strong>ID:</strong> <span className="font-mono text-xs">{viewingVehicle.transporter.id}</span></p>
                                        <p className="text-sm"><strong>Name:</strong> {viewingVehicle.transporter.profile?.fullName || 'N/A'}</p>
                                        <p className="text-sm"><strong>Email:</strong> {viewingVehicle.transporter.email}</p>
                                        <Button variant="link" size="sm" className="pl-0 pt-0 text-blue-500 hover:underline">
                                            <Link to={`/admin/users?search=${viewingVehicle.transporter.email}`}>View Transporter Profile</Link>
                                        </Button>
                                    </div>
                                )}

                                {viewingVehicle.images.length > 0 && (
                                    <div className="space-y-2 pt-4 border-t border-border">
                                        <h4 className="font-semibold text-primary">Gallery Images</h4>
                                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                            {viewingVehicle.images.map((image, index) => (
                                                <a key={index} href={resolveSrc(image.filePath)} target="_blank" rel="noopener noreferrer" className="block rounded-md overflow-hidden hover:opacity-90 transition-opacity">
                                                    <img src={resolveSrc(image.filePath)} alt={`Vehicle Image ${index + 1}`} className="w-full h-24 object-cover" />
                                                </a>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {viewingVehicle.documents.length > 0 && (
                                    <div className="space-y-2 pt-4 border-t border-border">
                                        <h4 className="font-semibold text-primary">Documents</h4>
                                        <ul className="text-sm list-disc pl-5">
                                            {viewingVehicle.documents.map((doc, index) => (
                                                <li key={index} className="text-muted-foreground">
                                                    <strong>{doc.type}:</strong> <a href={resolveSrc(doc.filePath)} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline ml-1">View Document</a>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        )}
                        <DialogFooter>
                            <Button onClick={() => setIsViewModalOpen(false)}>Close</Button>
                            {/* Optional: Add quick verify/reject buttons here if not verified */}
                            {!viewingVehicle?.verified && viewingVehicle?.id && (
                                <>
                                    <Button variant="success" onClick={() => handleVerify(viewingVehicle.id!)} disabled={verifyVehicleMutation.isLoading}>
                                        {verifyVehicleMutation.isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                        Verify
                                    </Button>
                                    <Button variant="destructive" onClick={() => handleReject(viewingVehicle.id!, "Rejected by Admin.")} disabled={rejectVehicleMutation.isLoading}>
                                        {rejectVehicleMutation.isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                        Reject
                                    </Button>
                                </>
                            )}
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}
        </div>
    );
};

export default AllVehiclesPage;